/**
 * Sparkline — tiny inline trend chart (v0.12.5).
 *
 * Renders a 12-point line/bar series as an inline SVG. No external chart
 * library — just hand-rolled SVG with smooth path + area fill + last-point
 * dot. Sized to fit inside a KPI tile (default 88×28).
 */

interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  color?: string;
  fill?: string;
  band?: 'green' | 'amber' | 'red';
  showDot?: boolean;
  ariaLabel?: string;
}

const BAND_COLORS = {
  green: { line: '#166534', fill: 'rgba(22, 101, 52, 0.12)' },
  amber: { line: '#92400E', fill: 'rgba(146, 64, 14, 0.12)' },
  red:   { line: '#B91C1C', fill: 'rgba(185, 28, 28, 0.12)' },
};

export function Sparkline({
  data,
  width = 88,
  height = 28,
  color,
  fill,
  band = 'green',
  showDot = true,
  ariaLabel,
}: SparklineProps) {
  if (!data || data.length === 0) {
    return <span aria-label={ariaLabel ?? 'No trend data'} style={{ width, height, display: 'inline-block' }} />;
  }
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = Math.max(1, max - min);
  const pad = 2;
  const stepX = (width - pad * 2) / Math.max(1, data.length - 1);
  const lineColor = color ?? BAND_COLORS[band].line;
  const fillColor = fill ?? BAND_COLORS[band].fill;
  const points = data.map((v, i) => {
    const x = pad + i * stepX;
    const y = pad + (height - pad * 2) * (1 - (v - min) / range);
    return [x, y] as const;
  });
  const path = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`).join(' ');
  const areaPath = `${path} L ${points.at(-1)?.[0] ?? 0} ${height - pad} L ${pad} ${height - pad} Z`;
  const last = points[points.length - 1];
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-label={ariaLabel}
      style={{ overflow: 'visible' }}
    >
      <path d={areaPath} fill={fillColor} />
      <path d={path} fill="none" stroke={lineColor} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      {showDot && last && (
        <circle cx={last[0]} cy={last[1]} r="2.5" fill={lineColor} stroke="white" strokeWidth="1" />
      )}
    </svg>
  );
}
